<script setup>
import '@campact/camper/core/CmprButton';
import '@campact/camper/cta/CmprPrefilledArea';
import CmprSelect from '@cta/frontend/components/CmprSelect.vue';
import CmprRadio from '@cta/frontend/components/CmprRadio.vue';

import StepItem from '@cta/donate_call_component/components/StepItem.vue';
import { computed, inject } from 'vue';

const store = inject('store');

const props = defineProps({
  paymentIntervalOptions: {
    type: Array,
    default() {
      return [];
    },
  },
  paymentIntervalDescriptions: {
    type: Array,
    default() {
      return [];
    },
  },
  paymentIntervalUi: {
    type: String,
    default: 'select',
  },
  analyticsIdPrefix: {
    type: String,
    default: 'sponsor-call',
  },
  showPleaseSelectOption: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['try-open', 'commit', 'validate']);

const paymentIntervalSummary = computed(() => props.paymentIntervalOptions.find(
  (option) => option.value === store.values.paymentInterval,
)?.text ?? '');
const paymentIntervalStepTitle = computed(() => {
  if (!store.paymentIntervalStepValid) return 'Abbuchungsrhythmus wählen';
  return 'Abbuchungsrhythmus ausgewählt';
});

function validatePaymentIntervalIfNeeded() {
  if (store.paymentIntervalErrorUnlocked && store.errors.paymentInterval) emit('validate');
}

function paymentIntervalDescriptionFor(value) {
  return props.paymentIntervalDescriptions.find(
    (option) => option.value === value).text;
}

</script>

<template>
  <div class="payment-interval-step">
    <StepItem
      :number="2"
      :opened="store.paymentIntervalStepOpen"
      :title="paymentIntervalStepTitle"
      tls-notice="Die Übermittlung Deiner Daten erfolgt sicher via SSL-Verschlüsselung."
      @try-open="emit('try-open')"
    >
      <template #opened>
        <CmprSelect
          v-if="props.paymentIntervalUi == 'select'"
          v-model="store.values.paymentInterval"
          aria-label="Abbuchungsrhythmus"
          :class="{'is-invalid': store.paymentIntervalErrorsUnlocked && store.errors.paymentInterval}"
          data-analytics-context="payment-interval-step"
          :data-analytics-id="`${analyticsIdPrefix}-form-payment-interval-dropdown`"
          data-analytics-inner-text="payment-interval"
          @change="validatePaymentIntervalIfNeeded"
        >
          <option
            v-if="!store.paymentIntervalStepValid && showPleaseSelectOption"
            disabled="disabled"
            :value="store.values.paymentInterval"
          >
            Bitte wählen
          </option>
          <option
            v-for="option in paymentIntervalOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.text }}
          </option>
        </CmprSelect>

        <div
          v-else
          aria-label="Abbuchungsrhythmus"
          class="payment-step__body"
        >
          <div
            v-for="option in paymentIntervalOptions"
            :key="option.value"
            class="payment-interval"
            :class="{'payment-interval--selected': store.values.paymentInterval === option.value}"
          >
            <div class="cmpr-radio-control">
              <CmprRadio
                :id="`payment-interval-${option.value}`"
                v-model="store.values.paymentInterval"
                data-analytics-context="payment-interval-step"
                :data-analytics-id="`${analyticsIdPrefix}-form-payment-interval-radio-${option.value}`"
                data-analytics-inner-text="payment-interval"
                name="payment-interval"
                :value="option.value"
                @change="validatePaymentIntervalIfNeeded"
              />
              <label
                class="payment-interval-title cmpr-radio-control__label"
                :for="`payment-interval-${option.value}`"
              >
                {{ option.text }}
              </label>
            </div>
            <span>
              {{ paymentIntervalDescriptionFor(option.value) }}
            </span>
          </div>
        </div>

        <cmpr-form-hint
          v-if="store.paymentIntervalErrorUnlocked && store.errors.paymentInterval"
          variant="error"
        >
          {{ store.errors.paymentInterval }}
        </cmpr-form-hint>

        <cmpr-button
          class="w-100 mt-60"
          data-analytics-context="payment-interval-step"
          :data-analytics-id="`${analyticsIdPrefix}-form-payment-interval-commit-button`"
          size="small"
          variant="secondary"
          @click.prevent="emit('commit')"
        >
          Weiter
        </cmpr-button>
      </template>

      <template #closed>
        <cmpr-prefilled-area
          v-if="!!store.values.paymentInterval"
          class="payment-interval-step__summary"
          editable="editable"
          variant="success"
        >
          <div class="fw-bold">
            {{ paymentIntervalSummary }}
          </div>
        </cmpr-prefilled-area>
      </template>
    </StepItem>
  </div>
</template>

<style scoped lang="scss">
.payment-step__body {
  display: flex;
  flex-direction: column;
  gap: var(--cmpr-space-60);
}

.payment-interval {
  background: var(--cmpr-color-white);
  border: var(--cmpr-border-width-20) solid var(--cmpr-color-neutral-30);
  border-radius: var(--cmpr-border-radius-20);
  display: flex;
  flex-direction: column;
  gap: var(--cmpr-space-60);
  padding: var(--cmpr-space-60);

  &-title {
    color: var(--cmpr-color-text);
    font-size: var(--cmpr-font-size-30);
    font-weight: var(--cmpr-font-weight-30);
    line-height: var(--cmpr-font-line-height-40);
  }

  &--selected {
    background: var(--cmpr-color-secondary-10);
    border: var(--cmpr-border-width-20) solid var(--cmpr-color-secondary-80);

    .payment-interval-title {
      color: var(--cmpr-color-secondary-80);
    }
  }
}
</style>
